// 소셜 로그인 콜백 수신 화면.
// 백엔드 /auth/social/callback/:provider 는 URL fragment(#linkToken=...&provider=...) 로 본 화면에 토큰을 전달한다.
// 본 화면은 fragment 를 즉시 파싱하고 location.hash 를 비워(history 와 리퍼러에 토큰이 남지 않도록) 휴대폰 인증 화면으로 이동한다.
// 정책: 소셜 인증 후에도 휴대폰 OTP 인증이 필수다(주문/수령 연락처 확보). 따라서 콜백 직후 휴대폰 번호 입력(/login)으로 보낸다.

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SOCIAL_PROVIDERS, isSocialProviderName } from "@oo/shared";
import { resolveLogin } from "@/features/auth/authApi.ts";
import { useAuthStore } from "@/stores/authStore.ts";

// 라우트 진입 시 한 번만 동작하는 화면.
// 화면 자체는 짧은 로딩 텍스트만 보여주고 곧바로 다음 라우트로 이동한다.
export default function SocialCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 토큰 출처는 두 가지다:
    //  - 네이티브(딥링크): NativeBridge 가 openchuksan://... 를 파싱해 location.state 로 전달.
    //  - 웹: 서버가 URL fragment(#linkToken=...&provider=kakao)로 전달.
    const navState = location.state as { linkToken?: string; provider?: string } | null;
    let linkToken = navState?.linkToken ?? null;
    let providerRaw: string | null = navState?.provider ?? null;

    if (!linkToken) {
      // 웹 fragment 파싱.
      const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
      const params = new URLSearchParams(hash);
      linkToken = params.get("linkToken");
      providerRaw = params.get("provider");
    }

    // 즉시 fragment 제거 (브라우저 히스토리 / 추후 리퍼러 전송에서 토큰 노출 방지).
    window.history.replaceState(null, "", window.location.pathname);

    if (!linkToken || !isSocialProviderName(providerRaw)) {
      setError("로그인 정보를 받지 못했습니다. 처음부터 다시 시도해 주세요.");
      return;
    }

    // 이미 이 소셜 계정으로 가입한 회원이면 휴대폰 OTP/약관 없이 바로 로그인한다(소셜 재로그인).
    // 신규면 휴대폰 인증 화면으로 보내며 link 정보를 동반한다(약관 단계까지 전달되어 함께 제출됨).
    void (async () => {
      try {
        const res = await resolveLogin({ linkToken });
        if (res.status === "login") {
          setSession({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
            expiresAtSec: res.expiresAtSec,
            user: { id: res.user.id, displayName: res.user.displayName, tier: res.user.tier },
          });
          navigate("/", { replace: true });
          return;
        }
        navigate("/login/phone", {
          replace: true,
          state: { socialLink: { provider: providerRaw, linkToken } },
        });
      } catch {
        setError("로그인 처리에 실패했습니다. 처음부터 다시 시도해 주세요.");
      }
    })();
  }, [navigate, setSession, location.state]);

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col items-center justify-center gap-4 px-6 py-10">
      {error ? (
        <>
          <p className="text-base text-danger-700">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="h-12 rounded-md bg-brand-600 px-6 text-base font-medium text-white"
          >
            로그인 화면으로
          </button>
        </>
      ) : (
        <p className="text-base text-ink-600">로그인 처리 중입니다...</p>
      )}
    </main>
  );
}

// import 만으로 unused 처리되지 않도록 명시 (eslint no-unused-vars 안전망).
// 본 상수는 향후 분기 확장 시 참고용.
export const SUPPORTED_PROVIDERS = SOCIAL_PROVIDERS;
